import React, { useEffect, useMemo, useState } from "react"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { useParams, useNavigate, useLocation } from "react-router-dom"
import { toast } from "sonner"
import { Navbar } from "@/components/layout/Navbar"
import { PageLayout } from "@/components/layout/PageLayout"
import { MainContent } from "@/components/layout/MainContent"
import { PlayerListItem } from "@/components/layout/PlayerListItem"
import { useGamesStore } from "@/stores/games.store"
import { useAuthStore } from "@/stores/auth.store"
import { GameRegistrationButton } from "@/components/games/GameRegistrationButton"
import { Game, GameStatus, ListType } from "@/types/game"
import { gameService } from "@/services/game.service"
import { gameRatingService } from "@/services/game-rating.service"
import { teamService } from "@/services/team.service"
import { Button } from "@/components/ui/button"
import { MobileEnrollmentActionBar } from "@/components/ui/mobile-enrollment-action-bar"
import { Skeleton } from "@/components/ui/skeleton"
import { Textarea } from "@/components/ui/textarea"
import { MemberStarRating } from "@/components/ui/member-star-rating"
import {
  ActionMenu,
  ActionMenuItem,
  ActionMenuSeparator,
} from "@/components/ui/action-menu"
import { ConfirmDialog, useConfirmDialog } from "@/components/ui/confirm-dialog"
import { ExternalLink } from "@/components/ui/external-link"
import { Footer } from "@/components/layout/Footer"
import { AmbientBackground } from "@/components/ui/ambient-background"
import { EventCheckInPanel } from "@/components/check-in/EventCheckInPanel"
import { EventCheckInDoneCard } from "@/components/check-in/EventCheckInDoneCard"
import { GameRatingsDoneCard } from "@/components/games/GameRatingsDoneCard"
import { shouldShowEventCheckInPrompt } from "@/utils/event-check-in.utils"
import { invalidateGameParticipantQueries } from "@/utils/query-invalidation"
import { cn } from "@/lib/utils"

import { OptimizedImage } from "@/components/ui/optimized-image"
import { EntityAttachmentsPanel } from "@/components/shared/EntityAttachmentsPanel";
import { GameGalleryPanel } from "@/components/games/GameGalleryPanel";
import { OrganizerTeamCard } from "@/components/training/training-session-layout";
import type { StatusBadgeVariant } from "@/components/ui/status-badge"
import { formatGameDateVisual, canRegister, shouldShowRateTeamAction, hasUserCompletedGameTeamRatings, getGamePath, SPORTS_OPTIONS, PENDING_PAYMENT_STATUSES, WAITING_STATUSES, VISIBLE_PENDING_PAYMENT_STATUSES, gameToCardProps } from "@/utils/game.utils"
import { getTeamPath } from "@/utils/team.utils"
import { getChargedAmount } from "@/utils/payment.utils"
import { getSportCategoryLabel } from "@/utils/sport-categories"
import { getSportPositionLabel, sportHasPositions } from "@/utils/sport-positions"



interface Participant {
  id: string
  userId?: string
  name: string
  email?: string
  avatar?: string
  status: string
  joinedAt: string
  teamRole?: string | null
  preferredPosition?: string | null
  paymentExpiresAt?: string | null
}

interface ParticipantsState {
  confirmed: Participant[]
  waiting: Participant[]
  total: number
  confirmedCount: number
  waitingCount: number
}

import { getSportTheme } from "@/utils/game.utils"
import { formatBrShortDateTime } from "@/utils/br-datetime"
import { InterestListRevealPanel } from "@/components/games/interest-list-reveal-panel"
import { GameTeamSubscriptionReminder } from "@/components/games/GameTeamSubscriptionReminder"
import { GameTeamSubscriptionPromo } from "@/components/games/GameTeamSubscriptionPromo"
import { GameMyPreferredPositionField } from "@/components/games/GameMyPreferredPositionField"
import { Icon } from "@/components/ui/icon"


function formatShortPtDate(date: Date): string {
  return formatBrShortDateTime(date);
}

function isGameFinishedForFeedback(game: Game): boolean {
  if (game.status === GameStatus.CANCELLED) return false
  const end = game.gameEndDate ? new Date(game.gameEndDate) : new Date(game.gameDate)
  return end < new Date() || game.status === GameStatus.COMPLETED
}

function formatFeedTime(dateIso: string): string {
  const dt = new Date(dateIso)
  const diffMs = Date.now() - dt.getTime()
  const minutes = Math.floor(diffMs / (1000 * 60))
  if (minutes < 1) return "agora"
  if (minutes < 60) return `${minutes}m`
  const hours = Math.floor(minutes / 60)
  if (hours < 24) return `${hours}h`
  const days = Math.floor(hours / 24)
  if (days < 7) return `${days}d`
  return dt.toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit" })
}

function getInitials(name: string): string {
  return name
    .split(" ")
    .map((part) => part[0])
    .join("")
    .slice(0, 2)
    .toUpperCase()
}

/** Quebra no hífen só onde o título é renderizado no mobile (ver `<span className="md:hidden">`). */
function renderGameTitleWithHyphenBreaks(name: string): React.ReactNode {
  const parts = name.split(/\s*[-–—]\s*/).map((p) => p.trim())
  if (parts.length < 2) {
    return name
  }
  return (
    <>
      {parts.map((part, idx) => (
        <React.Fragment key={idx}>
          {idx > 0 && (
            <>
              <br />
              <span aria-hidden="true">- </span>
            </>
          )}
          {part}
        </React.Fragment>
      ))}
    </>
  )
}

export default function GameDetailsPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const location = useLocation()
  const queryClient = useQueryClient()
  const { user } = useAuthStore()
  const { games } = useGamesStore()
  const deleteConfirmDialog = useConfirmDialog()
  const [game, setGame] = useState<Game | null>(null)
  const [participants, setParticipants] = useState<ParticipantsState | null>(null)
  const [verificationCode, setVerificationCode] = useState<string | null>(null)
  const [detailsLoading, setDetailsLoading] = useState(true)
  const [userTeamRole, setUserTeamRole] = useState<string | null>(null)
  const [paymentNowTs, setPaymentNowTs] = useState(() => Date.now())

  const getPaymentTimeLeftLabel = (paymentExpiresAt?: string | null) => {
    if (!paymentExpiresAt) return "sem prazo"
    const diffMs = new Date(paymentExpiresAt).getTime() - paymentNowTs
    if (diffMs <= 0) return "expirado"

    const totalSeconds = Math.floor(diffMs / 1000)
    const hours = Math.floor(totalSeconds / 3600)
    const minutes = Math.floor((totalSeconds % 3600) / 60)
    const seconds = totalSeconds % 60

    if (hours > 0) {
      return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`
    }
    return `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`
  }

  useEffect(() => {
    const interval = setInterval(() => setPaymentNowTs(Date.now()), 1000)
    return () => clearInterval(interval)
  }, [])

  // Status de participante com cache (evita flicker "Participar" → "Já Inscrito")
  const participantStatusQuery = useQuery({
    queryKey: ["participantStatus", user?.id, [id]],
    queryFn: () => gameService.getParticipantStatusBatch([id!]),
    enabled: !!user && !!id,
    staleTime: 1000 * 60,
    gcTime: 1000 * 60 * 10,
  })
  const participantInfo = participantStatusQuery.data?.[id!]
  const isRegistered = !!participantInfo
  const userParticipantStatus = participantInfo?.status ?? null
  const [feedbackStars, setFeedbackStars] = useState<number | null>(null)
  const [feedbackComment, setFeedbackComment] = useState("")
  const [feedbackPublic, setFeedbackPublic] = useState(false)
  const [replyDrafts, setReplyDrafts] = useState<Record<string, string>>({})
  const [openReplyFor, setOpenReplyFor] = useState<string | null>(null)
  const [checkInSuccessOpen, setCheckInSuccessOpen] = useState(false)

  const gameFeedbackQuery = useQuery({
    queryKey: ["gameFeedback", id, user?.id],
    queryFn: () => gameService.getFeedback(id!),
    enabled: !!id && !!user && userParticipantStatus === "CONFIRMED",
    staleTime: 1000 * 30,
  })

  const ratingProgressQuery = useQuery({
    queryKey: ["gameRatingsProgress", id],
    queryFn: () => gameRatingService.getProgress(id!),
    enabled: !!id && !!user && userParticipantStatus === "CONFIRMED",
    staleTime: 1000 * 30,
  })

  const ratingsSummaryQuery = useQuery({
    queryKey: ["myGameRatingsSummary", user?.id, id],
    queryFn: async () => {
      const res = await gameService.findUserParticipatedGamesEnriched({
        scope: "all",
        limit: 200,
      })
      return res.games.find((g) => g.id === id)?.myTeammateRatingsSummary ?? null
    },
    enabled: !!id && !!user && userParticipantStatus === "CONFIRMED",
    staleTime: 1000 * 30,
  })

  useEffect(() => {
    const my = gameFeedbackQuery.data?.myFeedback
    if (my) {
      setFeedbackStars(my.stars)
      setFeedbackComment(my.comment ?? "")
      setFeedbackPublic(my.isPublic)
      return
    }
    setFeedbackStars(null)
    setFeedbackComment("")
    setFeedbackPublic(false)
  }, [
    gameFeedbackQuery.data?.myFeedback?.stars,
    gameFeedbackQuery.data?.myFeedback?.comment,
    gameFeedbackQuery.data?.myFeedback?.isPublic,
  ])

  const submitFeedbackMutation = useMutation({
    mutationFn: async () => {
      if (!id) throw new Error("Jogo inválido")
      if (feedbackStars == null) throw new Error("Selecione uma nota")
      return gameService.submitFeedback(id, {
        stars: feedbackStars,
        comment: feedbackComment.trim(),
        isPublic: feedbackPublic,
      })
    },
    onSuccess: async () => {
      toast.success("Comentário salvo com sucesso")
      await queryClient.invalidateQueries({ queryKey: ["gameFeedback", id, user?.id] })
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.message || "Não foi possível salvar seu comentário")
    },
  })

  const feedbackReactionMutation = useMutation({
    mutationFn: async ({
      feedbackId,
      reaction,
    }: {
      feedbackId: string
      reaction: "LIKE" | "DISLIKE"
    }) => {
      if (!id) throw new Error("Jogo inválido")
      return gameService.setFeedbackReaction(id, feedbackId, reaction)
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["gameFeedback", id, user?.id] })
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.message || "Não foi possível reagir ao comentário")
    },
  })

  const replyFeedbackMutation = useMutation({
    mutationFn: async ({ feedbackId, comment }: { feedbackId: string; comment: string }) => {
      if (!id) throw new Error("Jogo inválido")
      return gameService.replyFeedback(id, feedbackId, comment)
    },
    onSuccess: async (_, vars) => {
      setReplyDrafts((prev) => ({ ...prev, [vars.feedbackId]: "" }))
      setOpenReplyFor(null)
      await queryClient.invalidateQueries({ queryKey: ["gameFeedback", id, user?.id] })
      toast.success("Resposta enviada")
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.message || "Não foi possível enviar a resposta")
    },
  })

  const deleteFeedbackMutation = useMutation({
    mutationFn: async (feedbackId: string) => {
      if (!id) throw new Error("Jogo inválido")
      return gameService.deleteFeedback(id, feedbackId)
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["gameFeedback", id, user?.id] })
      toast.success("Comentário excluído")
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.message || "Não foi possível excluir o comentário")
    },
  })

  const [listCountdown, setListCountdown] = useState<{
    days: number
    hours: number
    minutes: number
    seconds: number
  } | null>(null)

  const {
    data: gameData,
    isLoading: isGameLoading,
    isError: isGameError,
    error: gameError,
  } = useQuery({
    queryKey: ["gameDetails", id],
    enabled: !!id,
    // Usa jogo já carregado na Home (store ou cache de publicGames) para exibir imediatamente
    // e evitar o "aparecer do nada" — transição suave ao vir da listagem.
    initialData: () => games.find((g) => g.id === id),
    placeholderData: (previousData) => {
      if (previousData) return previousData
      const cached = games.find((g) => g.id === id)
      if (cached) return cached
      const queriesData = queryClient.getQueriesData<Game[]>({ queryKey: ["publicGames"] })
      for (const [, data] of queriesData) {
        if (Array.isArray(data)) {
          const found = data.find((g) => g.id === id)
          if (found) return found
        }
      }
      return undefined
    },
    queryFn: () => gameService.findOne(id!),
    staleTime: 1000 * 5,
    gcTime: 1000 * 60 * 5,
  })

  useEffect(() => {
    if (isGameError) {
      console.error("Erro ao carregar jogo:", gameError)
      navigate("/")
    }
  }, [isGameError, gameError, navigate])

  useEffect(() => {
    if (!gameData) return
    setGame(gameData)
  }, [gameData])

  useEffect(() => {
    if (location.hash !== "#galeria" || !id || !gameData) return

    void (async () => {
      await queryClient.invalidateQueries({ queryKey: ["gameGallery", id] });
      await queryClient.refetchQueries({ queryKey: ["gameGallery", id] });
      window.setTimeout(() => {
        document.getElementById("galeria")?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 150);
    })();
  }, [location.hash, id, gameData, queryClient]);

  useEffect(() => {
    const loadDetails = async () => {
      if (!id || !gameData) return

      try {
        setDetailsLoading(true)
        const [participantsResult, roleResult] = await Promise.all([
          gameService
            .getParticipants(id)
            .catch(() => ({
              confirmed: [],
              waiting: [],
              confirmedCount: 0,
              waitingCount: 0,
              total: 0,
            })),
          user && gameData.teamId ? teamService.getMyRole(gameData.teamId).catch(() => null) : Promise.resolve(null),
        ])
        setParticipants(participantsResult)
        setUserTeamRole(roleResult)
      } catch (error) {
        console.error("Erro ao carregar detalhes do jogo:", error)
      } finally {
        setDetailsLoading(false)
      }
    }

    loadDetails()
  }, [id, gameData, user])

  // Código de verificação quando usuário está inscrito (depende do cache de status)
  useEffect(() => {
    if (!id || !user || !isRegistered) {
      setVerificationCode(null)
      return
    }
    gameService
      .getUserVerificationCode(id)
      .then(setVerificationCode)
      .catch(() => setVerificationCode(null))
  }, [id, user, isRegistered])



  // Countdown para quando a lista será aberta (para exibir na mensagem de lista fechada)
  // Deve estar ANTES de qualquer retorno condicional
  useEffect(() => {
    if (!game) {
      setListCountdown(null)
      return
    }

    const now = new Date()
    
    // Verificar se a lista já iniciou
    let isListOpen = false
    if (game.listType === ListType.DIRECT) {
      const listOpenAt = game.listOpenAt ? new Date(game.listOpenAt) : null
      if (!listOpenAt) {
        isListOpen = true
      } else {
        isListOpen = now >= listOpenAt
      }
    } else if (game.listType === ListType.INTEREST) {
      // Para interesse, a lista só aparece após listRevealAt (revelação da lista)
      const listRevealAt = game.listRevealAt ? new Date(game.listRevealAt) : null
      if (!listRevealAt) {
        // Sem data: alinhado a canViewList — só quem tem permissão vê a lista; demais ficam no placeholder
        isListOpen = false
      } else {
        isListOpen = now >= listRevealAt
      }
    } else {
      isListOpen = true
    }

    // Verificar se o usuário tem permissão para ver antes do início (só lista direta)
    const canViewBeforeStart = user && (
      userTeamRole === 'ADMIN' ||
      userTeamRole === 'OWNER' ||
      userTeamRole === 'MEMBER_VIP' ||
      user?.id === game.userId
    )

    // Interesse: na página pública do jogo todos veem o mesmo bloco até listRevealAt (sem lista 0/N).
    // Pré-revelação para staff: rota /participants.
    const canViewList =
      game.listType === ListType.INTEREST ? isListOpen : isListOpen || canViewBeforeStart

    if (canViewList) {
      setListCountdown(null)
      return
    }

    // Interesse: countdown fica no InterestListRevealPanel (mesmo bloco da página /interest/success)
    if (game.listType === ListType.INTEREST) {
      setListCountdown(null)
      return
    }

    const updateCountdown = () => {
      const now = new Date()
      let targetDate: Date | null = null

      if (game.listType === ListType.DIRECT) {
        targetDate = game.listOpenAt ? new Date(game.listOpenAt) : new Date(game.createdAt)
      }

      if (!targetDate) {
        setListCountdown(null)
        return
      }

      const diff = targetDate.getTime() - now.getTime()

      if (diff <= 0) {
        setListCountdown(null)
        return
      }

      const days = Math.floor(diff / (1000 * 60 * 60 * 24))
      const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60))
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60))
      const seconds = Math.floor((diff % (1000 * 60)) / 1000)

      setListCountdown({ days, hours, minutes, seconds })
    }

    updateCountdown()
    const interval = setInterval(updateCountdown, 1000)

    return () => clearInterval(interval)
  }, [game, user, userTeamRole])

  const listAvailabilityHint = useMemo(() => {
    if (!game) return null

    let targetDate: Date | null = null
    let title = ""
    let description = ""

    if (game.listType === ListType.DIRECT) {
      targetDate = game.listOpenAt ? new Date(game.listOpenAt) : null
      title = "Abertura da lista"
      description = "A lista de participantes ficará visível em"
    } else if (game.listType === ListType.INTEREST) {
      targetDate = game.listRevealAt ? new Date(game.listRevealAt) : null
      title = "Divulgação da lista"
      description = "A lista de participantes será divulgada em"
    }

    if (!targetDate || targetDate.getTime() <= Date.now()) return null

    return {
      title,
      description,
      dateLabel: formatShortPtDate(targetDate),
    }
  }, [game])

  if (isGameLoading && !game) {
    return (
      <PageLayout 
        className="bg-background relative overflow-x-hidden min-h-screen"
        backgroundBlobs={<AmbientBackground />}
      >
        <Navbar showAvatar={!!user} />
        <MainContent containerClassName="max-w-6xl">
          <div className="flex flex-col gap-6 lg:gap-8">
            <section className="relative overflow-hidden rounded-3xl border border-border/60 shadow-lg shadow-black/5 dark:shadow-none">
              <Skeleton className="h-[min(48vw,280px)] sm:h-[min(38vw,340px)] md:h-[360px] w-full rounded-none" />
            </section>
            <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,1fr)_min(100%,340px)] gap-6 lg:gap-8">
              <div className="flex flex-col gap-6 min-w-0 order-1 xl:order-1">
                <Skeleton className="h-40 rounded-2xl" />
                <Skeleton className="h-72 rounded-2xl" />
              </div>
              <div className="flex flex-col gap-4 order-2 xl:order-2">
                <Skeleton className="h-48 rounded-2xl" />
                <Skeleton className="h-60 rounded-2xl" />
              </div>
            </div>
          </div>
        </MainContent>
        <Footer />
      </PageLayout>
    )
  }

  if (!game) {
    return null
  }

  const dateVisual = formatGameDateVisual(game.gameDate, game.gameEndDate)
  const passFeesToUser = (game as any).passFeesToUser ?? true
  const displayPrice =
    game.pricePerPlayer <= 0
      ? 0
      : getChargedAmount(game.pricePerPlayer, "PIX", passFeesToUser)
  const priceFormatted = new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL",
  }).format(displayPrice)

  const isOwner = game.userId === user?.id
  const canEdit = isOwner || userTeamRole === 'ADMIN' || userTeamRole === 'OWNER'
  const isTeamOwner =
    !!user && !!game.team?.userId && game.team.userId === user.id
  const isTeamMember = isTeamOwner || !!userTeamRole
  const canBalanceSquads =
    Boolean(user && game.team) &&
    (isTeamOwner ||
      userTeamRole === 'ADMIN' ||
      userTeamRole === 'OWNER')
  const isConfirmedParticipant = userParticipantStatus === "CONFIRMED"
  const ratingsCompletion = {
    myTeammateRatingsSummary: ratingsSummaryQuery.data ?? undefined,
    ratingProgress: ratingProgressQuery.data,
    myGameFeedbackSubmitted: !!gameFeedbackQuery.data?.myFeedback,
  }
  const ratingsKnownComplete = hasUserCompletedGameTeamRatings(ratingsCompletion)
  const ratingsStatusLoading =
    isConfirmedParticipant &&
    !ratingsKnownComplete &&
    (ratingsSummaryQuery.isLoading ||
      ratingProgressQuery.isLoading ||
      gameFeedbackQuery.isLoading)
  const canRateTeam =
    shouldShowRateTeamAction(game, {
      userId: user?.id,
      teamUserRole: userTeamRole,
      isParticipant: isConfirmedParticipant,
      ...ratingsCompletion,
    }) && !ratingsStatusLoading
  const canReviewGame =
    !!user &&
    userParticipantStatus === "CONFIRMED" &&
    isGameFinishedForFeedback(game)
  const canSelfCheckIn = !!user && userParticipantStatus === "CONFIRMED"
  const userCheckedInAt = participantInfo?.checkedInAt ?? null
  const canShowSelfCheckInPanel =
    canSelfCheckIn &&
    shouldShowEventCheckInPrompt({
      eventStartsAt: game.gameDate,
      eventEndsAt: game.gameEndDate,
      hasLocationCoordinates:
        game.locationLat != null && game.locationLng != null,
      checkedInAt: userCheckedInAt,
    })
  const showCheckInSection = canShowSelfCheckInPanel || checkInSuccessOpen
  const showCheckInDoneCard =
    isConfirmedParticipant && !!userCheckedInAt && !canShowSelfCheckInPanel
  const showRatingsDoneCard =
    isConfirmedParticipant && ratingsKnownComplete && !ratingsStatusLoading
  const gameFeedback = gameFeedbackQuery.data
  const visibleGameFeedbacks = (gameFeedback?.feedbacks ?? []).filter((entry) => entry.isPublic)
  const publicFeedbackAverage =
    visibleGameFeedbacks.length > 0
      ? visibleGameFeedbacks.reduce((sum, entry) => sum + entry.stars, 0) /
        visibleGameFeedbacks.length
      : 0
  const showGameActionsMenu =
    canEdit || (canBalanceSquads && !!game.team) || canRateTeam

  // Verificar se o usuário pode se registrar
  const userCanRegister = user ? canRegister(game, {
    userId: user.id,
    userTeamRole: userTeamRole || undefined,
  }) : false

  const sportTheme = getSportTheme(game.sport, game.name + ' ' + (game.team?.name || ''))
  const sportCategoryLabel = getSportCategoryLabel(game.sport, game.sportCategory ?? null)
  // Ocupação real de vagas (confirmados + pendentes que ocupam vaga)
  const occupiedCount = participants?.confirmedCount ?? participants?.confirmed?.length
  // Título da lista pública: só confirmados (PENDING_PAYMENT não entra na exibição)
  const confirmedDisplayCount =
    participants?.confirmed?.filter((p) => p.status === "CONFIRMED").length ??
    participants?.confirmed?.length ??
    0

  const heroCover =
    game.coverImage?.trim() ||
    game.team?.photo ||
    GAME_COVER_PLACEHOLDER
  const listTypeLabel =
    game.listType === ListType.DIRECT ? "Lista direta" : "Lista de interesse"
  const listTypeShortLabel =
    game.listType === ListType.DIRECT ? "Direta" : "Interesse"
  const gameCardStatus = gameToCardProps(
    game,
    undefined,
    occupiedCount,
    isRegistered,
    userParticipantStatus,
    participantInfo?.paymentExpiresAt ?? null,
    undefined,
    { userId: user?.id, userTeamRole },
  )
  const heroStatusColor =
    HERO_STATUS_COLORS[gameCardStatus.statusVariant] ?? HERO_STATUS_COLORS.neutral
  const participantsStat =
    game.maxParticipants > 0
      ? `${confirmedDisplayCount}/${game.maxParticipants}`
      : String(confirmedDisplayCount ?? occupiedCount ?? 0)
  const heroDateLabel = `${dateVisual.dayName}, ${dateVisual.day} ${dateVisual.month} · ${dateVisual.startTime}${
    dateVisual.endTime ? ` – ${dateVisual.endTime}` : ""
  }`
  const nowForShare = new Date()
  const gameDateForShare = new Date(game.gameDate)
  const gameEndDateForShare = game.gameEndDate ? new Date(game.gameEndDate) : null
  const gameEndedForShare = gameEndDateForShare
    ? gameEndDateForShare < nowForShare
    : gameDateForShare < nowForShare
  const showShareButton =
    game.status !== GameStatus.CANCELLED && !gameEndedForShare

  const buildWhatsAppShareMessage = () => {
    const gamePath = getGamePath(game)
    const paymentUrl = `${window.location.origin}${gamePath}`

    const participantsList = (participants?.confirmed ?? [])
      .map((participant, index) => {
        const isConfirmed = participant.status === "CONFIRMED"
        const isPendingPayment = PENDING_PAYMENT_STATUSES.includes(participant.status as any)
        const statusIcon = isConfirmed ? " ✅" : isPendingPayment ? " ⏳" : ""
        return `${index + 1}. ${participant.name}${statusIcon}`
      })
      .join("\n")

    const participantsSection = participantsList || "1."

    return [
      `${game.name}, das ${dateVisual.startTime}${dateVisual.endTime ? ` às ${dateVisual.endTime}` : ""} 💪🔥`,
      "",
      `📍 Local: ${game.location}`,
      "",
      `👥 Vagas: ${game.maxParticipants > 0 ? game.maxParticipants : occupiedCount ?? 0}`,
      `💰 Valor: ${priceFormatted}`,
      "💳 Pagamento:",
      `👉 ${paymentUrl}`,
      "",
      "⚠️ Importante:",
      "Coloque seu nome na lista somente após o pagamento, ok?",
      "",
      "Participantes:",
      participantsSection,
    ].join("\n")
  }

  const handleCopyShareList = async () => {
    try {
      const message = buildWhatsAppShareMessage()
      await navigator.clipboard.writeText(message)
      toast.success("lista copiada")
    } catch {
      toast.error("não foi possível copiar a lista")
    }
  }

  const handleShareGame = async () => {
    if (!game) return
    const url = `${window.location.origin}${getGamePath(game)}`
    const sportLabel =
      SPORTS_OPTIONS.find((s) => s.value === game.sport)?.label ??
      game.sport ??
      "Jogo"
    const { dayName, day, month, startTime } = formatGameDateVisual(
      game.gameDate,
      game.gameEndDate,
    )
    const shareTitle = game.name || `${sportLabel} — LIZTA`
    const shareText = `${sportLabel} · ${dayName}, ${day} ${month} às ${startTime}${game.team?.name ? ` · ${game.team.name}` : ""}`
    try {
      if (navigator.share) {
        await navigator.share({
          title: shareTitle,
          text: shareText,
          url,
        })
      } else {
        await navigator.clipboard.writeText(`${shareText}\n${url}`)
        toast.success("Link copiado para a área de transferência!")
      }
    } catch (error) {
      if (error instanceof Error && error.name !== "AbortError") {
        console.error("Erro ao compartilhar:", error)
      }
    }
  }



  // Pode exibir a lista se já iniciou OU se tem permissão especial
  const canViewList = (() => {
    if (!game) return false
    const now = new Date()
    
    if (game.listType === ListType.DIRECT) {
      const listOpenAt = game.listOpenAt ? new Date(game.listOpenAt) : null
      const isListOpen = !listOpenAt || now >= listOpenAt
      const canViewBeforeStart = user && (
        userTeamRole === 'ADMIN' ||
        userTeamRole === 'OWNER' ||
        userTeamRole === 'MEMBER_VIP' ||
        user?.id === game.userId
      )
      return isListOpen || canViewBeforeStart
    }
    
    if (game.listType === ListType.INTEREST) {
      // Lista pública só após listRevealAt (evita "Participantes (0/N)" antes da revelação).
      const listRevealAt = game.listRevealAt ? new Date(game.listRevealAt) : null
      if (!listRevealAt) {
        return false
      }
      return now >= listRevealAt
    }
    
    return true
  })()

  const participantsPanel = canViewList ? (
    !user ? (
      <div className="bg-card-surface p-8 rounded-xl shadow-xl shadow-black/5 dark:shadow-none border border-card-border hover:shadow-2xl hover:shadow-primary/5 transition-all flex-1 flex flex-col items-center justify-center text-center gap-6 min-h-[200px]">
        <div className="w-20 h-20 rounded-full bg-muted/30 flex items-center justify-center shrink-0">
          <Icon name="lock_open" />
        </div>
        <div className="flex flex-col gap-2">
          <h3 className="text-foreground text-xl font-bold">Lista de Participantes Bloqueada</h3>
          <p className="text-text-muted text-base max-w-md">
            Faça login para visualizar a lista de participantes deste jogo.
          </p>
        </div>
        <Button
          onClick={() => navigate("/login")}
          className="w-full h-12 text-base font-bold rounded-xl"
        >
          <Icon name="login" />
          Fazer Login
        </Button>
      </div>
    ) : (
      <div className="bg-card-surface p-8 rounded-xl shadow-xl shadow-black/5 dark:shadow-none border border-card-border hover:shadow-2xl hover:shadow-primary/5 transition-all flex flex-col flex-1 min-h-0">
        <div className="flex flex-col flex-1 min-h-0">
          <h3 className="text-foreground text-xl font-bold mb-4 shrink-0">
            Participantes ({confirmedDisplayCount}
            {game.maxParticipants === 0 ? "" : `/${game.maxParticipants}`})
          </h3>
          <div className="flex flex-col gap-2 flex-1 min-h-0 overflow-y-auto scrollbar-hide">
            {(() => {
              const visibleParticipants = (participants?.confirmed ?? []).filter(
                (p) =>
                  p.status === "CONFIRMED" ||
                  VISIBLE_PENDING_PAYMENT_STATUSES.includes(p.status as any),
              )

              return (
                <>
                  {visibleParticipants.slice(0, 12).map((participant) => {
                    let roleLabel = "Participante"
                    if (participant.teamRole) {
                      const roleMap: Record<string, string> = {
                        OWNER: "Proprietário",
                        ADMIN: "Administrador",
                        MEMBER_VIP: "Membro VIP",
                        MEMBER: "Membro",
                        VISITOR: "Visitante",
                        CONVIDADO: "Convidado",
                      }
                      roleLabel = roleMap[participant.teamRole] || participant.teamRole
                    }

                    const paymentCountdown = VISIBLE_PENDING_PAYMENT_STATUSES.includes(
                      participant.status as any,
                    )
                      ? getPaymentTimeLeftLabel(participant.paymentExpiresAt)
                      : null

                    const positionLabel =
                      sportHasPositions(game.sport, game.sportCategory) &&
                      participant.preferredPosition
                        ? getSportPositionLabel(
                            game.sport,
                            participant.preferredPosition,
                            game.sportCategory,
                          )
                        : null

                    const roleParts = [roleLabel]
                    if (positionLabel) roleParts.push(positionLabel)
                    if (paymentCountdown) roleParts.push(paymentCountdown)

                    return (
                      <PlayerListItem
                        key={participant.id}
                        name={participant.name}
                        role={roleParts.join(" • ")}
                        avatar={participant.avatar || undefined}
                        status={
                          VISIBLE_PENDING_PAYMENT_STATUSES.includes(participant.status as any)
                            ? "pending_payment"
                            : "confirmed"
                        }
                      />
                    )
                  })}
                  {visibleParticipants.length > 12 ? (
                    <div className="rounded-lg border border-border bg-muted/20 px-3 py-2 text-center text-sm font-medium text-muted-foreground">
                      +{visibleParticipants.length - 12} participantes na lista completa
                    </div>
                  ) : null}
                </>
              )
            })()}
            {(() => {
              const now = new Date()
              const gameDate = new Date(game.gameDate)
              const gameEndDate = game.gameEndDate ? new Date(game.gameEndDate) : null
              const isGameEnded = gameEndDate ? gameEndDate < now : gameDate < now
              if (isGameEnded) return null

              const confirmedCount =
                participants?.confirmed.filter(
                  (p) =>
                    p.status === "CONFIRMED" ||
                    VISIBLE_PENDING_PAYMENT_STATUSES.includes(p.status as any),
                ).length || 0
              const emptySlots =
                game.maxParticipants === 0
                  ? 0
                  : Math.max(0, game.maxParticipants - confirmedCount)

              if (emptySlots === 0) return null

              return (
                <div className="flex items-center justify-between gap-3 rounded-lg border border-border border-dashed bg-muted/20 p-3">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-10 h-10 rounded-full bg-muted/30 border border-border border-dashed flex items-center justify-center text-muted-foreground/60 shrink-0">
                      <Icon name="person_add" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-foreground text-sm font-semibold">
                        {emptySlots} {emptySlots === 1 ? "vaga disponível" : "vagas disponíveis"}
                      </p>
                      <p className="text-text-muted text-xs">
                        Novos participantes aparecerão aqui quando entrarem no jogo.
                      </p>
                    </div>
                  </div>
                  <span className="inline-flex shrink-0 items-center gap-1.5 rounded-full bg-background px-2 py-1 text-[10px] font-bold text-muted-foreground border border-border">
                    <span className="h-1.5 w-1.5 rounded-full bg-muted-foreground/30"></span>
                    Aberto
                  </span>
                </div>
              )
            })()}
          </div>
          {(() => {
            const now = new Date()
            const gameDate = new Date(game.gameDate)
            const gameEndDate = game.gameEndDate ? new Date(game.gameEndDate) : null
            const listEnded = gameEndDate ? gameEndDate < now : gameDate < now
            if (listEnded || !game.allowWaitlist) return null
            const waiting = participants?.waiting ?? []
            if (waiting.length === 0) return null
            return (
              <div className="mt-4 pt-4 border-t border-orange-100/80 dark:border-orange-900/30 shrink-0">
                <h4 className="text-foreground text-sm font-bold mb-3 flex items-center gap-2">
                  <Icon name="hourglass_empty" />
                  Lista de espera ({waiting.length})
                </h4>
                <div className="flex flex-col gap-1 max-h-[min(40vh,320px)] overflow-y-auto scrollbar-hide">
                  {waiting.map((w) => (
                    <PlayerListItem
                      key={w.id}
                      name={w.name}
                      role="Aguardando vaga"
                      avatar={w.avatar || undefined}
                      status="waiting"
                    />
                  ))}
                </div>
              </div>
            )
          })()}
        </div>
        {participants &&
          (participants.confirmed.length > 0 || (participants.waiting?.length ?? 0) > 0) && (
            <div className="pt-4 mt-4 border-t border-border shrink-0">
              <Button
                onClick={() => navigate(getGamePath(game, "participants"))}
                className="w-full rounded-xl h-12"
              >
                Ver Lista Completa
              </Button>
            </div>
          )}
      </div>
    )
  ) : game.listType === ListType.INTEREST && game.listRevealAt ? (
    <div className="bg-card-surface p-8 rounded-xl shadow-xl shadow-black/5 dark:shadow-none border border-card-border hover:shadow-2xl hover:shadow-primary/5 transition-all flex flex-col flex-1 min-h-0">
      <h3 className="text-foreground text-xl font-bold mb-4 shrink-0">
        Participantes ({confirmedDisplayCount}
        {game.maxParticipants === 0 ? "" : `/${game.maxParticipants}`})
      </h3>
      <div className="flex flex-1 flex-col items-center justify-center min-h-[200px]">
        <div className="w-full flex justify-center">
          <InterestListRevealPanel
            listRevealAt={game.listRevealAt}
            centerHeader
            className="w-full max-w-md"
          />
        </div>
      </div>
    </div>
  ) : (
    <div className="bg-card-surface p-8 rounded-xl shadow-xl shadow-black/5 dark:shadow-none border border-card-border hover:shadow-2xl hover:shadow-primary/5 transition-all flex-1 flex flex-col items-center justify-center text-center gap-6 min-h-[200px]">
      <div className="w-20 h-20 rounded-full bg-muted/30 flex items-center justify-center shrink-0">
        <Icon name={game.listType === ListType.INTEREST ? "schedule" : "lock_open"} />
      </div>
      <div className="flex flex-col gap-2">
        <h3 className="text-foreground text-xl font-bold">
          {game.listType === ListType.INTEREST
            ? "Revelação da Lista"
            : "Lista de Participantes Indisponível"}
        </h3>
        <p className="text-text-muted text-base max-w-md">
          {(() => {
            let targetDate: Date | null = null
            if (game.listType === ListType.DIRECT) {
              targetDate = game.listOpenAt
                ? new Date(game.listOpenAt)
                : game.createdAt
                  ? new Date(game.createdAt)
                  : null
            } else if (game.listType === ListType.INTEREST) {
              targetDate = game.listRevealAt ? new Date(game.listRevealAt) : null
            }

            if (targetDate) {
              const formattedDate = formatBrShortDateTime(targetDate)
              return (
                <>
                  {game.listType === ListType.INTEREST ? (
                    <>
                      A lista de participantes será divulgada em
                      <br />
                      {formattedDate}
                    </>
                  ) : (
                    <>
                      A lista de participantes estará disponível em
                      <br />
                      {formattedDate}
                    </>
                  )}
                </>
              )
            }
            return game.listType === ListType.INTEREST
              ? "A data de revelação ainda não foi definida. Volte mais tarde."
              : "A lista de participantes estará disponível em breve."
          })()}
        </p>
      </div>

      {listCountdown && (
        <div className="flex items-center justify-center gap-2 mt-2">
          {listCountdown.days > 0 && (
            <>
              <div className="flex flex-col items-center justify-center bg-input-bg border border-card-border rounded-lg px-3 py-2 min-w-[55px] shadow-sm">
                <p className="text-text-muted dark:text-text-muted text-[8px] font-medium mb-1 uppercase tracking-wider">
                  Dias
                </p>
                <p className="text-primary dark:text-primary text-xl font-black tabular-nums leading-none">
                  {String(listCountdown.days).padStart(2, "0")}
                </p>
              </div>
              <span className="text-primary dark:text-primary text-lg font-bold">:</span>
            </>
          )}
          <div className="flex flex-col items-center justify-center bg-input-bg border border-card-border rounded-lg px-3 py-2 min-w-[55px] shadow-sm">
            <p className="text-text-muted dark:text-text-muted text-[8px] font-medium mb-1 uppercase tracking-wider">
              Horas
            </p>
            <p className="text-primary dark:text-primary text-xl font-black tabular-nums leading-none">
              {String(listCountdown.hours).padStart(2, "0")}
            </p>
          </div>
          <span className="text-primary dark:text-primary text-lg font-bold">:</span>
          <div className="flex flex-col items-center justify-center bg-input-bg border border-card-border rounded-lg px-3 py-2 min-w-[55px] shadow-sm">
            <p className="text-text-muted dark:text-text-muted text-[8px] font-medium mb-1 uppercase tracking-wider">
              Min
            </p>
            <p className="text-primary dark:text-primary text-xl font-black tabular-nums leading-none">
              {String(listCountdown.minutes).padStart(2, "0")}
            </p>
          </div>
          <span className="text-primary dark:text-primary text-lg font-bold">:</span>
          <div className="flex flex-col items-center justify-center bg-input-bg border border-card-border rounded-lg px-3 py-2 min-w-[55px] shadow-sm">
            <p className="text-text-muted dark:text-text-muted text-[8px] font-medium mb-1 uppercase tracking-wider">
              Seg
            </p>
            <p className="text-primary dark:text-primary text-xl font-black tabular-nums leading-none">
              {String(listCountdown.seconds).padStart(2, "0")}
            </p>
          </div>
        </div>
      )}
    </div>
  )

  const galleryPanel = id ? (
    <GameGalleryPanel
      gameId={id}
      userId={user?.id}
      canManage={canEdit}
      coverImageUrl={game.coverImage}
    />
  ) : null

  return (
    <PageLayout 
      className="bg-background relative overflow-x-hidden min-h-screen"
      backgroundBlobs={
        <AmbientBackground 
          blob1Color={sportTheme.blob1} 
          blob2Color={sportTheme.blob2} 
        />
      }
    >

      <Navbar showAvatar={!!user} />

      <MainContent 
        className={cn(
          (showCheckInSection || !isRegistered || verificationCode) &&
            "pb-40 lg:pb-20",
        )}
        containerClassName="max-w-6xl"
      >
        <div className={cn("flex flex-col gap-6 lg:gap-8 w-full", "animate-in fade-in duration-300")}>
            <section className="relative overflow-hidden rounded-3xl border border-border/60 shadow-lg shadow-black/5 dark:shadow-none">
              <div className="relative h-[min(48vw,280px)] sm:h-[min(38vw,340px)] md:h-[360px]">
                <div className="absolute top-4 right-4 z-10 flex gap-2">
                  {showShareButton && (
                    <Button
                      type="button"
                      variant="outline"
                      size="icon"
                      className="h-10 w-10 rounded-xl bg-white/15 border-white/35 text-white backdrop-blur-sm hover:bg-white/25 hover:text-white"
                      onClick={() => void handleShareGame()}
                      title="Compartilhar"
                      aria-label="Compartilhar"
                    >
                      <Icon name="share" />
                    </Button>
                  )}
                  {showGameActionsMenu && (
                    <ActionMenu
                      align="end"
                      triggerVariant="outline"
                      triggerTitle="Mais opções"
                      triggerButtonClassName="h-10 w-10 rounded-xl bg-white/15 border-white/35 text-white backdrop-blur-sm hover:bg-white/25 hover:text-white p-0 flex items-center justify-center"
                      trigger={
                        <Icon name="more_horiz" />
                      }
                    >
                      {canEdit && (
                        <ActionMenuItem
                          icon="content_copy"
                          onClick={() => void handleCopyShareList()}
                        >
                          Copiar lista (WhatsApp)
                        </ActionMenuItem>
                      )}
                      {canBalanceSquads && game.team && (
                        <ActionMenuItem
                          icon="groups"
                          onClick={() =>
                            navigate(
                              `${getTeamPath(game.team!, "squad-balance")}?gameId=${encodeURIComponent(game.id)}`,
                            )
                          }
                        >
                          Sortear equipes
                        </ActionMenuItem>
                      )}
                      {canRateTeam && (
                        <ActionMenuItem
                          icon="star"
                          onClick={() => navigate(getGamePath(id!, "rate-team"))}
                        >
                          Avaliar
                        </ActionMenuItem>
                      )}
                      {canEdit && (
                        <>
                          <ActionMenuSeparator />
                          <ActionMenuItem
                            icon="replay"
                            onClick={() =>
                              navigate(
                                `/games/create?duplicateFrom=${id}&teamId=${game.teamId}`,
                              )
                            }
                          >
                            Duplicar jogo
                          </ActionMenuItem>
                          <ActionMenuItem
                            icon="settings"
                            onClick={() =>
                              navigate(getGamePath(id!, "edit"), {
                                state: { returnTo: getGamePath(id!) },
                              })
                            }
                          >
                            Editar jogo
                          </ActionMenuItem>
                        </>
                      )}
                    </ActionMenu>
                  )}
                </div>
                <OptimizedImage
                  src={heroCover}
                  alt=""
                  priority
                  className="absolute inset-0 h-full w-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/95 via-black/50 to-black/20" />
                <div className="absolute inset-0 bg-gradient-to-r from-black/40 to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-5 sm:p-8 md:p-10 text-white">
                  <div className="flex flex-wrap items-center gap-2 mb-3">
                    <span
                      className={cn(
                        "inline-flex items-center rounded-full px-3 py-1 text-xs font-bold uppercase tracking-wide backdrop-blur-sm",
                        heroStatusColor,
                      )}
                    >
                      {gameCardStatus.statusLabel}
                    </span>
                    <GameMetaChip icon={sportTheme.icon} label={sportTheme.label} />
                    {sportCategoryLabel && (
                      <GameMetaChip icon="groups" label={sportCategoryLabel} />
                    )}
                  </div>
                  <h1 className="text-2xl sm:text-3xl md:text-4xl font-black tracking-tight leading-tight max-w-3xl">
                    <span className="md:hidden">{renderGameTitleWithHyphenBreaks(game.name)}</span>
                    <span className="hidden md:block">{game.name}</span>
                  </h1>
                  <p className="mt-2 text-sm sm:text-base font-medium text-white/90 flex items-center gap-1.5">
                    <Icon name="event" />
                    {heroDateLabel}
                  </p>
                </div>
              </div>
            </section>

            <div className="xl:hidden grid grid-cols-3 gap-2 sm:gap-3">
              <GameQuickStat icon="payments" label="Valor" value={priceFormatted} compact />
              <GameQuickStat icon="groups" label="Vagas" value={participantsStat} compact />
              <GameQuickStat icon="format_list_bulleted" label="Lista" value={listTypeShortLabel} compact />
            </div>

            {listAvailabilityHint && (
              <div className="rounded-2xl border border-primary/20 bg-primary/5 px-4 py-3">
                <p className="text-xs font-bold uppercase tracking-wider text-primary mb-1">
                  {listAvailabilityHint.title}
                </p>
                <p className="text-sm text-foreground">
                  {listAvailabilityHint.description}{" "}
                  <span className="font-semibold">{listAvailabilityHint.dateLabel}</span>
                </p>
              </div>
            )}

                {/* Main Grid — mobile: ordem única via max-xl:order-* + contents; desktop: 2 colunas xl */}
              <div className="flex w-full flex-col gap-6 lg:gap-8 items-stretch max-xl:[&>*]:w-full max-xl:[&>*]:min-w-0 xl:grid xl:grid-cols-[minmax(0,1fr)_min(100%,340px)] xl:items-start">
                  {/* Left Column: Details & Logistics */}
                  <div className="contents xl:flex xl:flex-col xl:gap-6 xl:min-w-0">
                    {/* Logistics Cards */}
                    {/* Details Grid */}
                    <div className="contents xl:flex xl:flex-col xl:gap-6">
                      {/* Game Info Card */}
                      <div className="max-xl:order-2 xl:order-none w-full bg-card-surface p-8 rounded-xl border border-card-border shadow-xl shadow-black/5 dark:shadow-none transition-all flex flex-col gap-6 group hover:shadow-2xl hover:shadow-primary/5">
                        <div className="flex items-start gap-4 mb-4">
                          <div
                            className={cn(
                              "w-11 h-11 shrink-0 rounded-full flex items-center justify-center transition-colors",
                              sportTheme.bg,
                              sportTheme.text)}
                          >
                            <Icon name={sportTheme.icon} />
                          </div>
                          <div className="flex-1 min-w-0">
                            <h3 className="text-foreground text-xl font-bold">Informações</h3>
                          </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                          <div className="flex flex-col gap-1 p-4 rounded-xl bg-muted/30 border border-border hover:border-border transition-colors">
                            <div className="flex items-center gap-2 text-muted-foreground mb-1">
                              <Icon name="calendar_month" />
                              <span className="text-xs font-bold tracking-wider">Data</span>
                            </div>
                            <p className="text-lg font-black text-foreground">
                              {dateVisual.dayName}, {dateVisual.day} {dateVisual.month}
                            </p>
                            <p className="text-sm font-medium text-muted-foreground">
                              {dateVisual.startTime} {dateVisual.endTime && `- ${dateVisual.endTime}`}
                            </p>
                          </div>

                          <div className="flex flex-col gap-1 p-4 rounded-xl bg-muted/30 border border-border hover:border-border transition-colors">
                            <div className="flex items-center gap-2 text-muted-foreground mb-1">
                              <Icon name="payments" />
                              <span className="text-xs font-bold tracking-wider">Valor</span>
                            </div>
                            <p className="text-lg font-black text-foreground">
                              {priceFormatted}
                            </p>
                            <p className="text-sm font-medium text-muted-foreground">
                              {game.pricePerPlayer <= 0
                                ? "por pessoa"
                                : passFeesToUser
                                  ? "por pessoa (inclui taxa)"
                                  : "por pessoa"}
                            </p>
                          </div>
                        </div>
                      </div>

                      {/* Location Card */}
                      {(() => {
                        const locationParts = game.location.split(" - ")
                        const locationName = locationParts[0]?.trim() || game.location
                        const locationAddress = locationParts.length > 1 ? locationParts.slice(1).join(" - ").trim() : null
                        const mapsQuery = encodeURIComponent(locationName)
                        const mapsLink = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(game.location)}`
                        const mapsEmbedUrl = import.meta.env.VITE_GOOGLE_MAPS_API_KEY
                          ? `https://www.google.com/maps/embed/v1/place?key=${import.meta.env.VITE_GOOGLE_MAPS_API_KEY}&q=${mapsQuery}`
                          : null
                        return (
                          <div className="max-xl:order-3 xl:order-none w-full bg-card-surface p-8 rounded-xl border border-card-border shadow-xl shadow-black/5 dark:shadow-none transition-all hover:shadow-2xl hover:shadow-primary/5 flex flex-col gap-6 group">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-3">
                                <div className="w-12 h-12 rounded-full bg-red-50 dark:bg-red-500/10 flex items-center justify-center text-red-500">
                                  <Icon name="location_on" />
                                </div>
                                <div>
                                  <h3 className="text-lg font-bold text-foreground leading-tight">{locationName}</h3>
                                  {locationAddress && (
                                    <p className="text-sm text-muted-foreground mt-0.5">{locationAddress}</p>
                                  )}
                                  <ExternalLink
                                    href={mapsLink}
                                    className="inline-flex items-center gap-1 text-sm font-medium text-primary hover:underline mt-2"
                                  >
                                    <Icon name="map" />
                                    Ver no mapa
                                  </ExternalLink>
                                </div>
                              </div>
                            </div>

                            <div className="w-full h-56 md:h-64 rounded-xl overflow-hidden relative border border-border">
                              {mapsEmbedUrl ? (
                                <iframe
                                  title="Local do jogo no mapa"
                                  src={mapsEmbedUrl}
                                  className="w-full h-full border-0"
                                  allowFullScreen
                                  loading="lazy"
                                  referrerPolicy="no-referrer-when-downgrade"
                                />
                              ) : (
                                <ExternalLink
                                  href={mapsLink}
                                  className="flex w-full h-full items-center justify-center bg-muted/30 hover:bg-muted/50 transition-colors"
                                >
                                  <Icon name="map" />
                                  <span className="ml-2 text-sm font-medium text-muted-foreground">Clique para ver no mapa</span>
                                </ExternalLink>
                              )}
                            </div>
                          </div>
                        )
                      })()}
                    </div>

                  {/* Timeline Component */}
                  {game && (
                    <div className="max-xl:order-4 xl:order-none w-full bg-card-surface rounded-xl p-8 shadow-xl shadow-black/5 dark:shadow-none border border-card-border hover:shadow-2xl hover:shadow-primary/5 transition-all flex flex-col">
                      <h3 className="text-foreground text-xl font-bold mb-6 shrink-0">Próximas Etapas</h3>
                        <div className="grid grid-cols-[40px_1fr] gap-x-2 flex-1">
                          {(() => {
                            const now = new Date()
                            const formatDateShort = (dateString?: string) =>
                              dateString ? formatBrShortDateTime(dateString) : null

                            // Verificar se o jogo já encerrou
                            const gameDate = new Date(game.gameDate)
                            const gameEndDate = game.gameEndDate ? new Date(game.gameEndDate) : null
                            const gameEnded = gameEndDate ? gameEndDate < now : gameDate < now

                            const steps: Array<{
                              icon: string
                              title: string
                              description?: string
                              date?: string
                              isActive: boolean
                              isPast: boolean
                            }> = []

                            if (game.listType === ListType.DIRECT) {
                              // Lista Direta: listOpenAt -> listCloseAt -> paymentDeadline -> gameDate
                              const listOpenAt = game.listOpenAt ? new Date(game.listOpenAt) : null
                              const listCloseAt = game.listCloseAt ? new Date(game.listCloseAt) : null
                              const paymentDeadline = game.paymentDeadline ? new Date(game.paymentDeadline) : null
                              const gameDate = new Date(game.gameDate)

                              // Step 1: Abertura da Lista
                              // Lista está ativa se:
                              // 1. Tem listOpenAt e está no período (entre listOpenAt e listCloseAt, se existir)
                              // 2. OU não tem listOpenAt mas o jogo está OPEN (lista sempre aberta)
                              const isListOpenActive = listOpenAt 
                                ? (now >= listOpenAt && (!listCloseAt || now < listCloseAt))
                                : (game.status === GameStatus.OPEN && now < gameDate)
                              // É passado se: já passou o listOpenAt (evento já iniciou) OU não tem listOpenAt mas jogo está OPEN
                              // Se está ativo, também é passado (prioriza ativo na renderização)
                              const isListOpenPast = isListOpenActive || (listOpenAt 
                                ? now >= listOpenAt 
                                : (game.status === GameStatus.OPEN && now < gameDate))
                              steps.push({
                                icon: "lock_open",
                                title: "Abertura da Lista",
                                description: "Período para realizar inscrições.",
                                date: formatDateShort(game.listOpenAt || game.createdAt) || undefined,
                                isActive: isListOpenActive,
                                isPast: isListOpenPast,
                              })

                              // Step 2: Fechamento da Lista
                              if (listCloseAt) {
                                const isListClosePast = now >= listCloseAt
                                steps.push({
                                  icon: "group",
                                  title: "Fechamento da Lista",
                                  description: "Após esse horário, novas inscrições não serão aceitas.",
                                  date: formatDateShort(game.listCloseAt) || undefined,
                                  isActive: false,
                                  isPast: isListClosePast,
                                })
                              }

                              // Step 3: Prazo de Pagamento
                              if (paymentDeadline) {
                                const isPaymentPast = now >= paymentDeadline
                                steps.push({
                                  icon: "payments",
                                  title: "Prazo Final para Pagamento",
                                  description: "Pagamento obrigatório para garantir a vaga.",
                                  date: formatDateShort(game.paymentDeadline) || undefined,
                                  isActive: false,
                                  isPast: isPaymentPast,
                                })
                              }
                            } else if (game.listType === ListType.INTEREST) {
                              // Manifestação de Interesse: interestStartAt -> interestEndAt -> listRevealAt -> paymentDeadline -> gameDate
                              const interestStartAt = game.interestStartAt ? new Date(game.interestStartAt) : null
                              const interestEndAt = game.interestEndAt ? new Date(game.interestEndAt) : null
                              const listRevealAt = game.listRevealAt ? new Date(game.listRevealAt) : null
                              const paymentDeadline = game.paymentDeadline ? new Date(game.paymentDeadline) : null

                              // Step 1: Manifestação de Interesse
                              const isInterestActive = interestStartAt ? now >= interestStartAt && (!interestEndAt || now < interestEndAt) : false
                              // É passado se: já passou o interestStartAt (evento já iniciou)
                              const isInterestPast = interestStartAt ? now >= interestStartAt : false
                              steps.push({
                                icon: "lock_open",
                                title: "Manifestação de Interesse",
                                description: "Período para indicar interesse em participar.",
                                date: formatDateShort(game.interestStartAt) || undefined,
                                isActive: isInterestActive,
                                isPast: isInterestPast,
                              })

                              // Step 2: Encerramento da Manifestação
                              if (interestEndAt) {
                                const isInterestEndPast = now >= interestEndAt
                                steps.push({
                                  icon: "group",
                                  title: "Encerramento da Manifestação",
                                  description: "Após esse horário, novas manifestações não serão aceitas.",
                                  date: formatDateShort(game.interestEndAt) || undefined,
                                  isActive: false,
                                  isPast: isInterestEndPast,
                                })
                              }

                              // Step 3: Divulgação da Lista
                              if (listRevealAt) {
                                const isRevealPast = now >= listRevealAt
                                steps.push({
                                  icon: "visibility",
                                  title: "Divulgação da Lista de Participantes",
                                  description: "Publicação dos participantes selecionados.",
                                  date: formatDateShort(game.listRevealAt) || undefined,
                                  isActive: false,
                                  isPast: isRevealPast,
                                })
                              }

                              // Step 4: Prazo de Pagamento
                              if (paymentDeadline) {
                                const isPaymentPast = now >= paymentDeadline
                                steps.push({
                                  icon: "payments",
                                  title: "Prazo Final para Pagamento",
                                  description: "Pagamento obrigatório para garantir a vaga.",
                                  date: formatDateShort(game.paymentDeadline) || undefined,
                                  isActive: false,
                                  isPast: isPaymentPast,
                                })
                              }
                            }

                            // Step Final: Dia do Jogo
                            const isGamePast = gameEnded || now >= gameDate
                            steps.push({
                              icon: "sports_score",
                              title: "Dia do Jogo",
                              description: "Início do evento.",
                              date: formatDateShort(game.gameDate) || undefined,
                              isActive: false,
                              isPast: isGamePast,
                            })

                            // Se o jogo já encerrou, forçar todos os steps a serem passados
                            if (gameEnded) {
                              steps.forEach(step => {
                                step.isActive = false
                                step.isPast = true
                              })
                            }

                            return steps.map((step, index) => {
                              const isLast = index === steps.length - 1
                              const isActive = step.isActive
                              const isPast = step.isPast

                              return (
                                <React.Fragment key={index}>
                                    <div className="flex flex-col items-center h-full relative">
                                    {/* Line */}
                                    {index > 0 && (
                                      <div className={`w-[2px] ${isPast ? "bg-primary" : "bg-muted"} h-6 mb-1`}></div>
                                    )}
                                    <div
                                      className={`size-10 shrink-0 overflow-hidden rounded-full flex items-center justify-center z-20 transition-all duration-300 ${
                                        isActive
                                          ? "bg-primary text-white shadow-md shadow-primary/20 scale-110"
                                          : isPast
                                          ? "bg-primary/10 text-primary border border-primary/20"
                                          : "bg-muted/30 text-muted-foreground/40 border border-border"
                                      }`}
                                    >
                                      <Icon name={step.icon} />
                                    </div>
                                    {!isLast && (
                                      <div
                                        className={`w-[2px] ${
                                          isPast || isActive ? "bg-primary" : "bg-muted"
                                        } h-full min-h-[40px] mt-1`}
                                      ></div>
                                    )}
                                  </div>
                                  <div className={`flex flex-1 flex-col pt-2 pb-8 ${isActive ? "opacity-100" : "opacity-60"}`}>
                                    <p
                                      className={`${
                                        isActive
                                          ? "text-foreground font-black text-lg"
                                          : isPast
                                          ? "text-foreground font-bold text-base"
                                          : "text-muted-foreground font-medium text-base"
                                      } leading-tight transition-colors duration-300`}
                                    >
                                      {step.title}
                                    </p>
                                    {step.description && (
                                      <p className="text-muted-foreground text-sm mt-1.5 font-medium leading-relaxed">{step.description}</p>
                                    )}
                                    {step.date && (
                                       <span className="inline-flex items-center gap-1.5 mt-2 bg-muted/30 rounded-full px-2.5 py-1 text-xs font-bold text-muted-foreground w-fit">
                                          <Icon name="calendar_today" />
                                          {step.date}
                                       </span>
                                    )}
                      </div>
                                </React.Fragment>
                              )
                            })
                          })()}
                        </div>
                      </div>
                    )}

                    <div className="max-xl:order-4 xl:order-none w-full">
                      <EntityAttachmentsPanel attachments={game.attachments} />
                    </div>

                    <div className="max-xl:order-8 xl:order-none w-full xl:hidden">{participantsPanel}</div>

                    <div className="max-xl:order-9 xl:order-none w-full">{galleryPanel}</div>

                    {canReviewGame && (
                      <div className="max-xl:order-10 xl:order-none w-full bg-card-surface p-8 rounded-xl border border-card-border shadow-xl shadow-black/5 dark:shadow-none hover:shadow-2xl hover:shadow-primary/5 transition-all flex flex-col gap-5">
                        <div className="flex items-start justify-between gap-3">
                          <div>
                            <h3 className="text-xl font-bold text-foreground">Comentários</h3>
                          </div>
                          <div className="text-right shrink-0">
                            <p className="text-xs text-muted-foreground">Média dos participantes</p>
                            <p className="text-lg font-black text-foreground">
                              {publicFeedbackAverage.toLocaleString("pt-BR", {
                                minimumFractionDigits: 1,
                                maximumFractionDigits: 1,
                              })}{" "}
                              <span className="text-sm font-semibold text-muted-foreground">/ 5</span>
                            </p>
                          </div>
                        </div>

                        {!gameFeedback?.myFeedback && (
                          <div className="rounded-xl border border-border bg-card p-4">
                            <p className="text-sm font-semibold text-foreground">Adicionar um comentário</p>
                            <div className="mt-3 flex flex-col gap-3 lg:flex-row lg:items-center">
                              <input
                                value={feedbackComment}
                                onChange={(e) => setFeedbackComment(e.target.value)}
                                maxLength={500}
                                placeholder="Escreva seu comentário"
                                disabled={submitFeedbackMutation.isPending}
                                className="h-11 w-full rounded-xl border border-border bg-background px-3 text-sm text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/40 disabled:opacity-60"
                              />
                              <div className="shrink-0">
                                <MemberStarRating value={feedbackStars ?? 0} onChange={setFeedbackStars} />
                              </div>
                              <Button
                                onClick={() => submitFeedbackMutation.mutate()}
                                disabled={
                                  submitFeedbackMutation.isPending ||
                                  feedbackStars == null ||
                                  !feedbackComment.trim()
                                }
                                className="h-11 rounded-xl font-semibold lg:px-6"
                              >
                                {submitFeedbackMutation.isPending ? "Enviando..." : "Comentar"}
                              </Button>
                            </div>
                            <p className="mt-2 text-xs text-muted-foreground">{feedbackComment.length}/500</p>
                          </div>
                        )}

                        {visibleGameFeedbacks.length ? (
                          <div className="overflow-hidden">                          
                            <div className="max-h-[36rem] overflow-y-auto">
                              {visibleGameFeedbacks.map((entry) => (
                                <div
                                  key={entry.id}
                                  className="py-4 border-b border-border last:border-b-0"
                                >
                                  <div className="flex items-start gap-3">
                                    <div className="relative shrink-0">
                                      {entry.userAvatar ? (
                                        <img
                                          src={entry.userAvatar}
                                          alt={entry.userName}
                                          className="h-10 w-10 rounded-full object-cover ring-1 ring-border"
                                        />
                                      ) : (
                                        <div className="h-10 w-10 rounded-full bg-muted flex items-center justify-center text-xs font-bold ring-1 ring-border">
                                          {getInitials(entry.userName)}
                                        </div>
                                      )}
                                      {entry.replies.length > 0 && (
                                        <span className="absolute left-1/2 top-11 -translate-x-1/2 h-[calc(100%-2rem)] w-px bg-muted" />
                                      )}
                                    </div>
                                    <div className="min-w-0 flex-1 pb-1">
                                      <div className="flex items-center justify-between gap-2">
                                        <div className="min-w-0 flex items-center gap-1.5">
                                          <p className="text-sm font-semibold text-foreground truncate leading-none">
                                            {entry.userName}
                                          </p>
                                          <span className="text-[11px] text-muted-foreground shrink-0">
                                            · {formatFeedTime(entry.updatedAt)}
                                          </span>
                                        </div>
                                        {entry.userId === user?.id ? (
                                          <ActionMenu
                                            align="end"
                                            triggerVariant="ghost"
                                            triggerTitle="Ações do comentário"
                                            triggerButtonClassName="h-7 w-7 rounded-full p-0 text-muted-foreground/70 hover:text-foreground"
                                            trigger={
                                              <Icon name="more_horiz" />
                                            }
                                          >
                                            <ActionMenuItem
                                              icon="delete"
                                              className="text-destructive focus:text-destructive"
                                              onClick={() => {
                                                deleteConfirmDialog.open({
                                                  title: "Excluir comentário?",
                                                  description:
                                                    "Essa ação remove seu comentário e não poderá ser desfeita.",
                                                  variant: "danger",
                                                  confirmText: "Excluir",
                                                  onConfirm: async () => {
                                                    await deleteFeedbackMutation.mutateAsync(entry.id)
                                                  },
                                                })
                                              }}
                                            >
                                              Excluir comentário
                                            </ActionMenuItem>
                                          </ActionMenu>
                                        ) : (
                                          <Icon name="more_horiz" />
                                        )}
                                      </div>
                                      <div className="mt-2.5 flex items-baseline gap-2">
                                        <span className="shrink-0 text-xs font-semibold text-amber-600 dark:text-amber-400">
                                          {entry.stars}★
                                        </span>
                                        {entry.comment && (
                                          <p className="text-sm text-foreground whitespace-pre-wrap break-words leading-relaxed">
                                            {entry.comment}
                                          </p>
                                        )}
                                      </div>

                                      <div className="mt-3 flex items-center gap-1 text-xs text-muted-foreground">
                                        <Button
                                          type="button"
                                          variant="ghost"
                                          size="sm"
                                          onClick={() =>
                                            feedbackReactionMutation.mutate({
                                              feedbackId: entry.id,
                                              reaction: "LIKE",
                                            })
                                          }
                                          disabled={feedbackReactionMutation.isPending}
                                          className={cn(
                                            "h-8 gap-1.5 px-2 font-medium",
                                            entry.myReaction === "LIKE" && "text-primary")}
                                        >
                                          <Icon name="thumb_up" />
                                          {entry.likesCount > 0 ? entry.likesCount : null}
                                        </Button>
                                        <Button
                                          type="button"
                                          variant="ghost"
                                          size="sm"
                                          onClick={() =>
                                            feedbackReactionMutation.mutate({
                                              feedbackId: entry.id,
                                              reaction: "DISLIKE",
                                            })
                                          }
                                          disabled={feedbackReactionMutation.isPending}
                                          className={cn(
                                            "h-8 gap-1.5 px-2 font-medium",
                                            entry.myReaction === "DISLIKE" && "text-primary")}
                                        >
                                          <Icon name="thumb_down" />
                                          {entry.dislikesCount > 0 ? entry.dislikesCount : null}
                                        </Button>
                                        <Button
                                          type="button"
                                          variant="ghost"
                                          size="sm"
                                          onClick={() =>
                                            setOpenReplyFor((prev) =>
                                              prev === entry.id ? null : entry.id,
                                            )
                                          }
                                          className="h-8 gap-1.5 px-2 font-medium"
                                        >
                                          <Icon name="chat_bubble" />
                                          Responder
                                        </Button>
                                      </div>

                                      {entry.replies.length > 0 && (
                                        <div className="mt-3 space-y-2.5 border-l border-border pl-3">
                                          {entry.replies.map((reply) => (
                                            <div key={reply.id} className="flex items-start gap-2.5">
                                              {reply.userAvatar ? (
                                                <img
                                                  src={reply.userAvatar}
                                                  alt={reply.userName}
                                                  className="h-7 w-7 rounded-full object-cover ring-1 ring-border"
                                                />
                                              ) : (
                                                <div className="h-7 w-7 rounded-full bg-muted flex items-center justify-center text-[10px] font-bold ring-1 ring-border">
                                                  {getInitials(reply.userName)}
                                                </div>
                                              )}
                                              <div className="min-w-0 flex-1 px-1 py-0.5">
                                                <div className="flex items-center gap-1.5">
                                                  <p className="text-xs font-semibold text-foreground truncate leading-none">
                                                    {reply.userName}
                                                  </p>
                                                  <span className="text-[10px] text-muted-foreground/90 shrink-0">
                                                    · {formatFeedTime(reply.createdAt)}
                                                  </span>
                                                </div>
                                                <p className="mt-1.5 text-xs text-muted-foreground whitespace-pre-wrap break-words leading-relaxed">
                                                  {reply.comment}
                                                </p>
                                              </div>
                                            </div>
                                          ))}
                                        </div>
                                      )}

                                      {openReplyFor === entry.id && (
                                        <div className="mt-3 rounded-xl border border-border bg-transparent p-3">
                                          <Textarea
                                            value={replyDrafts[entry.id] ?? ""}
                                            onChange={(e) =>
                                              setReplyDrafts((prev) => ({
                                                ...prev,
                                                [entry.id]: e.target.value,
                                              }))
                                            }
                                            maxLength={280}
                                            placeholder="Escreva uma resposta..."
                                            size="sm"
                                            className="min-h-[72px] text-xs"
                                          />
                                          <div className="mt-2 flex items-center justify-between gap-2">
                                            <span className="text-[10px] text-muted-foreground">
                                              {(replyDrafts[entry.id] ?? "").length}/280
                                            </span>
                                            <Button
                                              className="h-8 rounded-lg text-xs"
                                              disabled={
                                                replyFeedbackMutation.isPending ||
                                                !(replyDrafts[entry.id] ?? "").trim()
                                              }
                                              onClick={() =>
                                                replyFeedbackMutation.mutate({
                                                  feedbackId: entry.id,
                                                  comment: (replyDrafts[entry.id] ?? "").trim(),
                                                })
                                              }
                                            >
                                              Responder
                                            </Button>
                                          </div>
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        ) : null}
                      </div>
                    )}
                    </div>

                  {/* Right Column: Actions */}
                  <aside className="contents xl:flex xl:flex-col xl:gap-4 xl:sticky xl:top-24">
                    {game.team ? (
                      <div className="w-full max-xl:order-0 xl:order-none">
                        <OrganizerTeamCard team={game.team} />
                      </div>
                    ) : null}
                    {game.teamId && user && (
                      <div className="flex w-full flex-col gap-4 max-xl:order-1 xl:order-none">
                        <GameTeamSubscriptionReminder teamId={game.teamId} />
                        <GameTeamSubscriptionPromo
                          teamId={game.teamId}
                          isTeamMember={isTeamMember}
                        />
                      </div>
                    )}
                    <div className="max-xl:order-5 xl:order-none w-full rounded-2xl border border-card-border bg-card p-5 shadow-sm space-y-4">
                      <div>
                        <h2 className="text-sm font-bold uppercase tracking-wide text-muted-foreground">
                          Resumo
                        </h2>
                        <p className="text-xs text-muted-foreground mt-1">
                          {listTypeLabel}
                          {game.maxParticipants > 0 ? ` · ${game.maxParticipants} vagas` : ""}
                        </p>
                      </div>
                      <div className="hidden xl:grid grid-cols-2 gap-3">
                        <GameQuickStat icon="payments" label="Valor" value={priceFormatted} />
                        <GameQuickStat icon="groups" label="Vagas" value={participantsStat} />
                      </div>
                      {game.maxParticipants > 0 && (
                        <div className="space-y-2 pt-1">
                          <div className="flex items-center justify-between text-xs">
                            <span className="font-semibold text-muted-foreground">Ocupação</span>
                            <span className="font-bold tabular-nums text-foreground">
                              {Math.min(
                                100,
                                Math.round(
                                  ((occupiedCount ?? confirmedDisplayCount) / game.maxParticipants) * 100,
                                ),
                              )}
                              %
                            </span>
                          </div>
                          <div className="h-2 rounded-full bg-muted overflow-hidden">
                            <div
                              className="h-full rounded-full bg-primary transition-all duration-500"
                              style={{
                                width: `${Math.min(
                                  100,
                                  Math.round(
                                    ((occupiedCount ?? confirmedDisplayCount) / game.maxParticipants) * 100,
                                  ),
                                )}%`,
                              }}
                            />
                          </div>
                        </div>
                      )}
                    </div>
                    <div className="flex flex-col gap-4 w-full max-xl:order-6 xl:order-none">
                    {/* Status Card - Jogo Cancelado */}
                    {game.status === GameStatus.CANCELLED && (
                      <div className="bg-red-50 dark:bg-red-900/10 p-8 rounded-xl border border-red-200 dark:border-red-900/20 shadow-xl shadow-red-900/5 transition-all">
                        <div className="flex items-start gap-4 mb-4">
                          <div className="w-11 h-11 rounded-full bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 shrink-0 flex items-center justify-center">
                            <Icon name="block" />
                          </div>
                          <div className="flex-1">
                            <p className="text-red-600/80 dark:text-red-400/80 text-sm font-bold tracking-wide mb-2">Status</p>
                            <p className="text-red-700 dark:text-red-400 text-2xl font-black leading-none tracking-tight mb-3">Jogo Cancelado</p>
                            <p className="text-red-600/80 dark:text-red-400/80 text-sm font-medium">Este jogo foi cancelado pela organização.</p>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Status Card - Usuário não logado */}
                    {!user && game.status !== GameStatus.CANCELLED && (
                      <div className="bg-card-surface p-8 rounded-xl border border-card-border shadow-xl shadow-black/5 dark:shadow-none hover:shadow-2xl hover:shadow-primary/5 transition-all">
                        <div className="flex items-start gap-4 mb-4">
                          <div className="w-11 h-11 rounded-full bg-blue-500/10 text-blue-600 shrink-0 flex items-center justify-center">
                            <Icon name="info" />
                          </div>
                          <div className="flex-1">
                            <p className="text-text-muted text-sm font-medium mb-2">Status</p>
                            <div className="flex flex-col gap-1">
                              <p className="text-foreground text-xl font-bold">Acesso Restrito</p>
                              <p className="text-text-muted text-sm">Faça login para ver mais detalhes e participar.</p>
                            </div>
                          </div>
                        </div>
                        <Button
                          onClick={() => navigate("/login")}
                          className="w-full h-12 text-base font-bold rounded-xl"
                        >
                          <Icon name="login" />
                          Fazer Login
                        </Button>
                      </div>
                    )}

                    {/* Status Card - Já Inscrito, Aguardando Pagamento ou Lista de Espera */}
                    {user && userParticipantStatus && game.status !== GameStatus.CANCELLED && (
                      <>
                        {PENDING_PAYMENT_STATUSES.includes((userParticipantStatus ?? "") as any) ? (
                          <>
                            {(() => {
                              // Verificar se o jogo já encerrou
                              const now = new Date()
                              const gameDate = new Date(game.gameDate)
                              const gameEndDate = game.gameEndDate ? new Date(game.gameEndDate) : null
                              const gameEnded = gameEndDate ? gameEndDate < now : gameDate < now

                              if (gameEnded) {
                                return (
                                  <div className="bg-card-surface p-8 rounded-xl border border-card-border shadow-xl shadow-black/5 dark:shadow-none hover:shadow-2xl hover:shadow-primary/5 transition-all relative overflow-hidden group">
                                    <div className="absolute top-0 right-0 p-6 opacity-5 group-hover:scale-110 transition-transform duration-500">
                                      <Icon name="event_busy" />
                                    </div>
                                    <div className="flex items-start gap-4 relative z-10">
                                      <div className="w-12 h-12 rounded-full bg-muted/30 text-muted-foreground shrink-0 flex items-center justify-center">
                                        <Icon name="event_busy" />
                                      </div>
                                      <div className="flex-1">
                                        <p className="text-muted-foreground text-sm font-bold tracking-wide mb-2">Status</p>
                                        <p className="text-foreground text-2xl font-black leading-none tracking-tight mb-3">Jogo Encerrado</p>
                                        <p className="text-muted-foreground text-xs font-bold bg-muted/30 px-2 py-1 rounded-md w-fit">Inscrições fechadas</p>
                                      </div>
                                    </div>
                                  </div>
                                )
                              }

                              return (
                                <div className="bg-card-surface p-8 rounded-xl border border-card-border shadow-xl shadow-black/5 dark:shadow-none hover:shadow-2xl hover:shadow-primary/5 transition-all">
                                  <div className="flex items-center gap-2 mb-4">
                                    <span className="w-2 h-2 rounded-full bg-orange-500 animate-pulse"></span>
                                    <span className="text-orange-500 text-xs font-bold tracking-wider animate-pulse">Ação Necessária</span>
                                  </div>
                                  <div className="flex flex-col gap-3">
                                    <Button
                                      onClick={() => navigate(getGamePath(id!, 'checkout'))}
                                      className="w-full h-12 text-base font-semibold bg-amber-500 hover:bg-amber-600 text-white shadow-lg shadow-amber-500/25 hover:shadow-xl hover:shadow-amber-500/30 transition-all"
                                    >
                                      <Icon name="payments" />
                                      Realizar Pagamento
                                    </Button>
                                    <Button
                                      variant="outline"
                                      onClick={() => navigate(getGamePath(id!, 'cancel'))}
                                      
                                      className="w-full h-12 text-base font-medium text-red-600 dark:text-red-400 border-red-300 dark:border-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 hover:border-red-400 dark:hover:border-red-500 transition-colors"
                                    >
                                      <Icon name="cancel" />
                                      Cancelar participação
                                    </Button>
                                  </div>
                                </div>
                              )
                            })()}
                          </>
                        ) : WAITING_STATUSES.includes((userParticipantStatus ?? "") as any) ? (
                          <>
                            {(() => {
                                const now = new Date()
                                const gameDate = new Date(game.gameDate)
                                const gameEndDate = game.gameEndDate ? new Date(game.gameEndDate) : null
                                const gameEnded = gameEndDate ? gameEndDate < now : gameDate < now

                                if (gameEnded) {
                                  return (
                                  <div className="bg-card-surface p-8 rounded-xl border border-card-border shadow-xl shadow-black/5 dark:shadow-none hover:shadow-2xl hover:shadow-primary/5 transition-all relative overflow-hidden group">
                                    <div className="absolute top-0 right-0 p-6 opacity-5 group-hover:scale-110 transition-transform duration-500">
                                      <Icon name="event_busy" />
                                    </div>
                                    <div className="flex items-start gap-4 relative z-10">
                                      <div className="w-12 h-12 rounded-full bg-muted/30 text-muted-foreground shrink-0 flex items-center justify-center">
                                        <Icon name="event_busy" />
                                      </div>
                                      <div className="flex-1">
                                        <p className="text-muted-foreground text-sm font-bold tracking-wide mb-2">Status</p>
                                        <p className="text-foreground text-2xl font-black leading-none tracking-tight mb-3">Jogo Encerrado</p>
                                        <p className="text-muted-foreground text-xs font-bold bg-muted/30 px-2 py-1 rounded-md w-fit">Inscrições fechadas</p>
                                      </div>
                                    </div>
                                  </div>
                                  )
                                }

                                return (
                                  <div className="bg-card-surface p-8 rounded-xl border border-card-border shadow-xl shadow-black/5 dark:shadow-none hover:shadow-2xl hover:shadow-primary/5 transition-all">
                                    <div className="flex items-start gap-4 mb-4">
                                      <div className="w-11 h-11 rounded-full bg-orange-500/10 text-orange-600 shrink-0 flex items-center justify-center">
                                        <Icon name="hourglass_empty" />
                                      </div>
                                      <div className="flex-1">
                                        <p className="text-text-muted text-sm font-medium mb-2">Status</p>
                                        <div className="flex flex-col gap-1">
                                          <p className="text-foreground text-xl font-bold">Lista de Espera</p>
                                          <p className="text-text-muted text-sm">Você está na lista de espera.</p>
                                        </div>
                                      </div>
                                    </div>
                                    {(() => {
                                      // Se o jogo já iniciou, não mostrar botão de cancelar
                                      // Note: gameEnded já cobre gameDate <= now logicamente se considerarmos o fim, mas aqui queremos bloquear cancelamento
                                      // se iniciou (mesmo que não tenha acabado ainda, se tiver duração).
                                      // Mas se gameEnded for false, ainda pode ter started.
                                      // A lógica original era gameDate <= now.
                                      // Vamos manter a lógica de ocultar botão se gameDate <= now.
                                      if (gameDate <= now) return null

                                      return (
                                        <Button
                                          onClick={() => navigate(getGamePath(id!, 'cancel'))}
                                          variant="outline"
                                          className="w-full h-12 text-base font-medium text-red-600 dark:text-red-400 border-red-300 dark:border-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 hover:border-red-400 dark:hover:border-red-500 transition-colors"
                                        >
                                          <Icon name="cancel" />
                                          Cancelar participação
                                        </Button>
                                      )
                                    })()}
                                  </div>
                                )
                            })()}
                          </>
                        ) : (
                          <div className="bg-card-surface p-8 rounded-xl border border-card-border shadow-xl shadow-black/5 dark:shadow-none hover:shadow-2xl hover:shadow-primary/5 transition-all">
                            <div className="flex items-start gap-4 mb-4">
                              <div className="w-11 h-11 rounded-full bg-green-500/10 text-green-600 shrink-0 flex items-center justify-center">
                                <Icon name="check_circle" />
                              </div>
                              <div className="flex-1">
                                <p className="text-text-muted text-sm font-medium mb-2">Status</p>
                                <div className="flex flex-col gap-1">
                                  <p className="text-foreground text-xl font-bold">Já Inscrito</p>
                                  <p className="text-text-muted text-sm">Você está confirmado neste jogo.</p>
                                </div>
                              </div>
                            </div>
                            <GameMyPreferredPositionField
                              gameId={id!}
                              sport={game.sport}
                              sportCategory={game.sportCategory}
                              initialPosition={participantInfo?.preferredPosition}
                              readOnly={new Date(game.gameDate) <= new Date()}
                            />
                            <Button
                              onClick={() =>
                                navigate(
                                  getGamePath(id!, "success"),
                                  verificationCode ? { state: { verificationCode } } : undefined,
                                )
                              }
                              disabled={participantStatusQuery.isLoading}
                              className="w-full h-12 rounded-xl text-base font-medium"
                            >
                              <Icon name="receipt_long" />
                              Ver comprovante
                            </Button>
                            {(() => {
                              const now = new Date()
                              const gameDate = new Date(game.gameDate)
                              if (gameDate <= now) return null

                              return (
                                <Button
                                  onClick={() => navigate(getGamePath(id!, "cancel"))}
                                  type="button"
                                  variant="outline"
                                  className="w-full h-12 rounded-xl text-base font-medium text-red-600 dark:text-red-400 border-red-300 dark:border-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 hover:border-red-400 dark:hover:border-red-500 transition-colors mt-2"
                                >
                                  <Icon name="cancel" />
                                  Cancelar participação
                                </Button>
                              )
                            })()}
                          </div>
                        )} 
                      </>
                    )}

                    {/* Hero Action Card - Participar ou Ver Recibo */}
                    {user && !userParticipantStatus && game.status !== GameStatus.CANCELLED && (
                    <>
                      {/* Desktop: Card normal - Realizar Inscrição, Countdown ou Vagas Esgotadas */}
                      {game.maxParticipants > 0 && occupiedCount !== undefined && occupiedCount >= game.maxParticipants && !game.allowWaitlist && game.listType === ListType.DIRECT ? (
                        <div className="hidden lg:block bg-card-surface p-8 rounded-xl border border-card-border shadow-xl shadow-black/5 dark:shadow-none hover:shadow-2xl hover:shadow-primary/5 transition-all w-full">
                          <div className="flex items-start gap-4 mb-4">
                            <div className="w-11 h-11 rounded-full bg-red-500/10 text-red-600 shrink-0 flex items-center justify-center">
                              <Icon name="block" />
                            </div>
                            <div className="flex-1">
                              <p className="text-text-muted text-sm font-medium mb-2">Status</p>
                              <div className="flex flex-col gap-1">
                                <p className="text-foreground text-xl font-bold">Vagas Esgotadas</p>
                                <p className="text-text-muted text-sm">Não há vagas disponíveis para este jogo.</p>
                              </div>
                            </div>
                          </div>
                          <Button
                            variant="secondary"
                            className="w-full h-12 text-base font-bold"
                            disabled
                          >
                            Inscrições Encerradas
                          </Button>
                        </div>
                      ) : (
                        <div className="hidden lg:block bg-card-surface p-8 rounded-xl border border-card-border shadow-xl shadow-black/5 dark:shadow-none hover:shadow-2xl hover:shadow-primary/5 transition-all w-full">
                          {userCanRegister && (
                            <div className="flex items-center gap-2 mb-2">
                              <span className="w-2 h-2 rounded-full bg-orange-500 animate-pulse"></span>
                              <span className="text-orange-500 text-xs font-bold tracking-wider animate-pulse">Ação Necessária</span>
                            </div>
                          )}
                          <GameRegistrationButton
                            game={game}
                            user={user}
                            userTeamRole={userTeamRole}
                            isRegistered={isRegistered}
                            currentParticipantsCount={occupiedCount}
                            participantsLoading={detailsLoading}
                          />
                        </div>
                      )}


                    </>
                  )}

                  {showRatingsDoneCard && (
                    <GameRatingsDoneCard
                      className="max-xl:order-7 xl:order-none"
                      summary={ratingsCompletion.myTeammateRatingsSummary}
                      received={ratingProgressQuery.data?.myReceivedInGame}
                    />
                  )}

                  {canRateTeam && (
                    <div className="max-xl:order-7 xl:order-none w-full bg-card-surface p-8 rounded-xl border border-card-border shadow-xl shadow-black/5 dark:shadow-none hover:shadow-2xl hover:shadow-primary/5 transition-all">
                      <div className="flex items-start gap-4 mb-4">
                        <div className="w-11 h-11 rounded-full bg-amber-500/10 text-amber-600 shrink-0 flex items-center justify-center">
                          <Icon name="star" />
                        </div>
                        <div className="flex-1">
                          <p className="text-text-muted text-sm font-medium mb-2">Avaliação</p>
                          <div className="flex flex-col gap-1">
                            <p className="text-foreground text-xl font-bold">Avaliar equipe</p>
                            <p className="text-text-muted text-sm">
                              Dê suas notas para os participantes deste jogo.
                            </p>
                          </div>
                        </div>
                      </div>
                      <Button
                        onClick={() => navigate(getGamePath(id!, "rate-team"))}
                        className="w-full h-12 rounded-xl text-base font-medium"
                      >
                        <Icon name="star" />
                        Avaliar equipe
                      </Button>
                    </div>
                  )}

                  {showCheckInDoneCard && (
                    <EventCheckInDoneCard
                      className="max-xl:order-8 xl:order-none"
                      checkedInAt={userCheckedInAt}
                    />
                  )}

                  {showCheckInSection && (
                    <EventCheckInPanel
                      className="max-xl:order-8 xl:order-none"
                      countsForRanking
                      checkedInAt={userCheckedInAt}
                      eventStartsAt={game.gameDate}
                      eventEndsAt={game.gameEndDate}
                      hasLocationCoordinates={
                        game.locationLat != null && game.locationLng != null
                      }
                      onCheckIn={(latitude, longitude) =>
                        gameService.selfCheckIn(id!, latitude, longitude)
                      }
                      onResolveAlreadyCheckedIn={async () => {
                        const status = await gameService.getParticipantStatusBatch([id!])
                        return status[id!]?.checkedInAt ?? null
                      }}
                      onSuccess={() => {
                        invalidateGameParticipantQueries(queryClient, id!)
                      }}
                      onSuccessModalChange={setCheckInSuccessOpen}
                    />
                  )}

                  {/* Participants List - desktop sidebar */}
                  <div className="hidden xl:block w-full">{participantsPanel}</div>
                    </div>
                  </aside>
              </div>
          </div>
      </MainContent>
      <Footer />
      <ConfirmDialog
        isOpen={deleteConfirmDialog.isOpen}
        onClose={deleteConfirmDialog.close}
        onConfirm={deleteConfirmDialog.handleConfirm}
        title={deleteConfirmDialog.config?.title ?? ""}
        description={deleteConfirmDialog.config?.description ?? ""}
        confirmText={deleteConfirmDialog.config?.confirmText ?? "Excluir"}
        variant={deleteConfirmDialog.config?.variant ?? "danger"}
        isLoading={deleteConfirmDialog.isLoading}
      />
      
      {/* Mobile: Floating button - Moved outside MainContent to fix stacking context */}
      {user &&
        (!userParticipantStatus ||
          PENDING_PAYMENT_STATUSES.includes((userParticipantStatus ?? "") as any)) &&
        game.status !== GameStatus.CANCELLED && (
          <>
            {PENDING_PAYMENT_STATUSES.includes((userParticipantStatus ?? "") as any) ? (
              <MobileEnrollmentActionBar
                visible
                priceLabel={displayPrice > 0 ? priceFormatted : null}
                buttonLabel="Realizar Pagamento"
                buttonIcon="payments"
                onButtonClick={() => navigate(getGamePath(id!, "checkout"))}
                buttonClassName="bg-amber-500 hover:bg-amber-600 text-white shadow-lg shadow-amber-500/25"
              />
            ) : game.maxParticipants > 0 &&
              occupiedCount !== undefined &&
              occupiedCount >= game.maxParticipants &&
              !game.allowWaitlist &&
              game.listType === ListType.DIRECT ? (
              <MobileEnrollmentActionBar
                visible
                showActionRequired={false}
                buttonLabel="Vagas Esgotadas"
                buttonDisabled
                onButtonClick={() => {}}
              />
            ) : (
              <MobileEnrollmentActionBar
                visible
                priceLabel={
                  game.pricePerPlayer <= 0
                    ? userCanRegister
                      ? "Grátis"
                      : null
                    : priceFormatted
                }
                showActionRequired={userCanRegister}
                hint={userCanRegister ? "Confirme sua participação no jogo." : null}
                action={
                  <GameRegistrationButton
                    game={game}
                    user={user}
                    userTeamRole={userTeamRole}
                    isRegistered={isRegistered}
                    currentParticipantsCount={occupiedCount}
                    participantsLoading={detailsLoading}
                    variant="mobile"
                    hideHint
                  />
                }
              />
            )}
          </>
        )}
    </PageLayout>
  )
}

const GAME_COVER_PLACEHOLDER =
  "https://images.unsplash.com/photo-1612872087720-bb876e2e67d1?q=80&w=1600&auto=format&fit=crop"

const HERO_STATUS_COLORS: Record<StatusBadgeVariant, string> = {
  default: "bg-primary/90 text-white",
  secondary: "bg-secondary/90 text-white",
  neutral: "bg-muted-foreground/90 text-white",
  success: "bg-emerald-600/90 text-white",
  warning: "bg-amber-600/90 text-white",
  danger: "bg-red-600/90 text-white",
  info: "bg-blue-600/90 text-white",
  orange: "bg-orange-600/90 text-white",
  violet: "bg-violet-600/90 text-white",
  primary: "bg-primary/90 text-white",
}

function GameMetaChip({ icon, label }: { icon: string; label: string }) {
  return (
    <span className="inline-flex items-center gap-1 rounded-full bg-white/15 px-2.5 py-1 text-xs font-semibold text-white backdrop-blur-sm">
      <Icon name={icon} />
      {label}
    </span>
  )
}

function GameQuickStat({
  icon,
  label,
  value,
  highlight,
  compact,
}: {
  icon: string
  label: string
  value: string
  highlight?: boolean
  compact?: boolean
}) {
  return (
    <div
      className={cn(
        "rounded-xl border",
        compact ? "p-2.5 flex flex-col" : "p-3",
        highlight
          ? "border-amber-300/80 bg-amber-50/80 dark:border-amber-800/50 dark:bg-amber-950/30"
          : "border-border/80 bg-muted/30 dark:border-border")}
    >
      <Icon name={icon} className={cn(compact ? "text-[16px]" : "text-[18px]")} />
      <p
        className={cn(
          "font-bold uppercase tracking-wide text-muted-foreground",
          compact ? "text-[9px] mt-1.5" : "text-[10px] mt-2")}
      >
        {label}
      </p>
      <p
        className={cn(
          "font-black text-foreground tabular-nums leading-tight",
          compact ? "text-base truncate" : "text-lg")}
      >
        {value}
      </p>
    </div>
  )
}
